/*--External-- */
import React, { useCallback } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import _ from "lodash";
import { Button } from "@toddle-edu/ds-web";
import {
  PlaceholderFileUpArrowOutlined,
  VideoOutlined,
} from "@toddle-edu/ds-icons";

/*--Internal-- */
import DropzoneComponent from "UIComponents/DropzoneComponent";
import {
  ALLOWED_VIDEO_FILE_TYPES,
  DEFAULT_MEDIA_SUBMISSION_METHOD,
} from "Worksheet/modules/WorksheetConstants";
import { createAttachment } from "modules/Services";

/*--Relative-- */
import classes from "./AddVideoResponseButton.scss";

/**
 * AddVideoResponseButton component renders buttons for adding a video response,
 * either by recording a new video or uploading an existing one.
 **/
const AddVideoResponseButton = props => {
  const {
    t,
    startRecording,
    updateIsUploading,
    isPreview,
    fieldId,
    parentType,
    portalType,
    createAttachment,
    updateAttachments,
    updateLocalAttachment,
    submissionMethod,
  } = props;

  // * Create attachment based on the mode, when the file is uploaded
  const handleCreateAttachment = useCallback(
    async file => {
      if (isPreview) {
        updateLocalAttachment(file);
        updateIsUploading(false);
        return;
      }
      updateIsUploading(true);
      const attachments = await createAttachment({
        attachment: file,
        nodeId: fieldId,
        type: parentType,
        parentType,
        portalType,
      });
      updateAttachments(attachments);
    },
    [
      isPreview,
      fieldId,
      parentType,
      portalType,
      updateAttachments,
      createAttachment,
    ]
  );

  const showVideoRecorder = _.includes(
    ["record_or_upload", "record_only"],
    submissionMethod
  );
  const showVideoUploader = _.includes(
    ["record_or_upload", "upload_only"],
    submissionMethod
  );

  return (
    <div className={classes.videoContainer}>
      {showVideoRecorder && (
        <Button
          size="large"
          type="outlined"
          dsVersion="2.0"
          variant="neutral"
          icon={<VideoOutlined />}
          onClick={startRecording}
          testId={`worksheet-video-question-start-recording-button`}
        >
          {t("worksheet:add_video_response")}
        </Button>
      )}
      {showVideoUploader && (
        <DropzoneComponent
          multiple={false}
          accept={ALLOWED_VIDEO_FILE_TYPES}
          updateFilesCount={_.noop}
          showConfirmationDialogue={true}
          onSingleContentUploaded={handleCreateAttachment}
          updateIsUploading={updateIsUploading}
          renderComponent={
            <Button
              dsVersion="2.0"
              variant="neutral"
              type="outlined"
              size="large"
              icon={<PlaceholderFileUpArrowOutlined />}
            >
              {t("worksheet:upload_file", {
                type: t("common:video"),
              })}
            </Button>
          }
        />
      )}
    </div>
  );
};

AddVideoResponseButton.propTypes = {
  t: PropTypes.func.isRequired,
  startRecording: PropTypes.func.isRequired,
  isPreview: PropTypes.bool,
  updateIsUploading: PropTypes.func,
  fieldId: PropTypes.string,
  parentType: PropTypes.string,
  portalType: PropTypes.string,
  createAttachment: PropTypes.func,
  updateAttachments: PropTypes.func,
  updateLocalAttachment: PropTypes.func,
  submissionMethod: PropTypes.string,
};

AddVideoResponseButton.defaultProps = {
  t: _.noop,
  startRecording: _.noop,
  isPreview: false,
  updateIsUploading: _.noop,
  fieldId: "",
  parentType: "",
  portalType: "",
  createAttachment: _.noop,
  updateAttachments: _.noop,
  updateLocalAttachment: _.noop,
  submissionMethod: DEFAULT_MEDIA_SUBMISSION_METHOD,
};

const mapActionCreators = { createAttachment };

export default connect(null, mapActionCreators)(AddVideoResponseButton);
